/**
 * 📁 CategoryManager.js - Manage Quiz Categories
 * * Admin component for:
 * - Viewing all categories by species
 * - Adding new categories
 * - Editing existing categories
 * - Viewing question counts per category
 */

import React, { useState } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase-config'; // ✅ FIXED PATH
import './CategoryManager.css';

// ============================================================================
// CONSTANTS
// ============================================================================

const SPECIES_OPTIONS = [
  { value: 'manatee', label: '🦭 Manatee', color: '#10B981', project: 'manatee' },
  { value: 'dolphin', label: '🐬 Dolphin', color: '#00B4D8', project: 'dolphin' },
  { value: 'humpback', label: '🐋 Humpback', color: '#0C71C3', project: 'whale' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const CategoryManager = ({ categories, onClose }) => {
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    species: 'manatee',
    description: '',
    emoji: '',
    color: '#6366F1',
  });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Group categories by species
  const categoriesBySpecies = {
    manatee: categories.filter((c) => c.species === 'manatee'),
    dolphin: categories.filter((c) => c.species === 'dolphin'),
    humpback: categories.filter((c) => c.species === 'humpback'),
  };

  // =========================================================================
  // HANDLERS
  // =========================================================================

  const handleAdd = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      species: 'manatee',
      description: '',
      emoji: '',
      color: '#6366F1',
    });
    setShowForm(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      species: category.species || 'manatee',
      description: category.description || '',
      emoji: category.emoji || '',
      color: category.color || '#6366F1',
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCategory(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Category name is required');
      return;
    }

    setSaving(true);

    try {
      // Generate ID from name
      const id = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');

      const speciesOpt = SPECIES_OPTIONS.find((s) => s.value === formData.species);

      const data = {
        name: formData.name.trim(),
        species: formData.species,
        project: speciesOpt?.project || formData.species,
        description: formData.description.trim() || null,
        emoji: formData.emoji || null,
        color: formData.color || null,
        isActive: true,
        updatedAt: serverTimestamp(),
      };

      if (editingCategory) {
        // Update existing
        await updateDoc(doc(db, 'quizCategories', editingCategory.id), data);
      } else {
        // Create new
        data.id = id;
        data.questionCount = 0;
        data.createdAt = serverTimestamp();
        await addDoc(collection(db, 'quizCategories'), { ...data, id });
      }

      setShowForm(false);
      setEditingCategory(null);
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;

    // Check if category has questions
    if (confirmDelete.questionCount > 0) {
      alert(
        `Cannot delete category with ${confirmDelete.questionCount} questions. ` +
        'Please remove or reassign the questions first.'
      );
      setConfirmDelete(null);
      return;
    }

    try {
      await deleteDoc(doc(db, 'quizCategories', confirmDelete.id));
      setConfirmDelete(null);
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete: ' + error.message);
    }
  };

  // =========================================================================
  // RENDER: FORM
  // =========================================================================

  const renderForm = () => (
    <div className="cm-form">
      <h3>{editingCategory ? '✏️ Edit Category' : '➕ Add New Category'}</h3>

      <div className="cm-form-row">
        <div className="cm-field">
          <label>Category Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Manatee Ecology"
          />
        </div>

        <div className="cm-field">
          <label>Species *</label>
          <select
            value={formData.species}
            onChange={(e) => setFormData({ ...formData, species: e.target.value })}
          >
            {SPECIES_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="cm-field">
        <label>Description (optional)</label>
        <input
          type="text"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Brief description of this category"
        />
      </div>

      <div className="cm-form-row">
        <div className="cm-field">
          <label>Emoji (optional)</label>
          <input
            type="text"
            value={formData.emoji}
            onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
            placeholder="🌿"
            maxLength={4}
            style={{ width: '80px' }}
          />
        </div>

        <div className="cm-field">
          <label>Color (optional)</label>
          <input
            type="color"
            value={formData.color}
            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
            style={{ width: '60px', height: '40px', padding: '2px' }}
          />
        </div>
      </div>

      <div className="cm-form-actions">
        <button className="cm-btn cm-btn-secondary" onClick={handleCancel}>
          Cancel
        </button>
        <button
          className="cm-btn cm-btn-success"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '⏳ Saving...' : '💾 Save Category'}
        </button>
      </div>
    </div>
  );

  // =========================================================================
  // RENDER: DELETE MODAL
  // =========================================================================

  const renderDeleteModal = () => {
    if (!confirmDelete) return null;

    return (
      <div className="cm-modal-overlay" onClick={() => setConfirmDelete(null)}>
        <div className="cm-modal" onClick={(e) => e.stopPropagation()}>
          <h3>⚠️ Delete Category</h3>
          <p>Are you sure you want to delete "{confirmDelete.name}"?</p>
          {confirmDelete.questionCount > 0 && (
            <p className="cm-modal-warning">
              ⚠️ This category has {confirmDelete.questionCount} questions.
              You must reassign or delete them first.
            </p>
          )}
          <div className="cm-modal-actions">
            <button
              className="cm-btn cm-btn-secondary"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button
              className="cm-btn cm-btn-danger"
              onClick={handleDelete}
              disabled={confirmDelete.questionCount > 0}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // =========================================================================
  // MAIN RENDER
  // =========================================================================

  return (
    <div className="cm-container">
      {/* Header */}
      <div className="cm-header">
        <button className="cm-close" onClick={onClose}>
          ← Back
        </button>
        <h2>📁 Category Manager</h2>
        <button className="cm-btn cm-btn-primary" onClick={handleAdd}>
          ➕ Add Category
        </button>
      </div>

      <div className="cm-content">
        {/* Form (if visible) */}
        {showForm && renderForm()}

        {/* Categories by Species */}
        {SPECIES_OPTIONS.map((speciesOpt) => (
          <div key={speciesOpt.value} className="cm-species-section">
            <h3 style={{ color: speciesOpt.color }}>
              {speciesOpt.label}
              <span className="cm-count">
                ({categoriesBySpecies[speciesOpt.value].length} categories)
              </span>
            </h3>

            <div className="cm-category-grid">
              {categoriesBySpecies[speciesOpt.value].map((cat) => (
                <div key={cat.id} className="cm-category-card">
                  <div className="cm-category-header">
                    {cat.emoji && <span className="cm-category-emoji">{cat.emoji}</span>}
                    <span className="cm-category-name">{cat.name}</span>
                  </div>
                  <div className="cm-category-meta">
                    <span>{cat.questionCount || 0} questions</span>
                  </div>
                  {cat.description && (
                    <p className="cm-category-desc">{cat.description}</p>
                  )}
                  <div className="cm-category-actions">
                    <button
                      className="cm-btn-icon"
                      onClick={() => handleEdit(cat)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      className="cm-btn-icon danger"
                      onClick={() => setConfirmDelete(cat)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}

              {categoriesBySpecies[speciesOpt.value].length === 0 && (
                <div className="cm-empty">No categories yet</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Delete Modal */}
      {renderDeleteModal()}
    </div>
  );
};

export default CategoryManager;